import {Box, Pagination} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import React, {useEffect, type Dispatch, type SetStateAction} from 'react';
import {GSSKT_HOST} from '../../../../config/constants/hosts';
import type {RecruitmentsType} from '../../../../core/entities/recruitment/schema';
import {validateCookie} from '../../../../core/usecases/auth/checkOauthTokenExistence';
import {SpTopBottomBanner} from '../../../../shared/components/Banner/SpTopBottomBanner';
import {getBannerPositionsInRecruitInfoForSp} from '../../usecase/getBannerPositionsInRecruitingInfoForSp';
import {useHandleRecruitingInfoForSp} from '../../usecase/useHandleRecruitingInfoForSp';
import {CardForSp} from './Presenter/CardForSp';
import {CardSkeltonSP} from './Presenter/CardSkelton';
import {HeaderForSp} from './Presenter/HeaderForSp';

type Props = {
  defaultRecruitments?: RecruitmentsType;
};

const limit = 10;

export const RecruitingInfoForSP: React.FC<Props> = ({defaultRecruitments}) => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const {
    data,
    isLoading,
    industryCategories,
    recruitmentTags,
    recruitmentTypes,
    graduationYearsHots,
    filterCategories,
    filterGraduationYear,
    filterRecruitmentTags,
    filterRecruitmentTypes,
    recruitmentOrder,
    setFilterCategories,
    setFilterGraduationYear,
    setFilterRecruitmentTags,
    setFilterRecruitmentTypes,
    setRecruitmentOrder,
    isDrawerOpen,
    setIsDrawerOpen,
    isOrderDrawerOpen,
    setIsOrderDrawerOpen,
    currentPage,
    lastPage,
    setCurrentPage,
    initialized,
  } = useHandleRecruitingInfoForSp({
    contentsLimit: limit,
  });

  const handleFilterChange = (
    id: number,
    setter: Dispatch<SetStateAction<number[]>>,
  ) => {
    setter((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const baseUrl = `${GSSKT_HOST}/recruiting_info`;
  const canonicalUrl = baseUrl;
  const router = useRouter();
  const {order, page} = router.query;

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    }
  }, [order, page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Head>
        <title key="title">募集を探す | 外資就活ドットコム</title>
        <meta key="og:title" property="og:title" content="募集を探す" />
        <meta key="og:url" property="og:url" content={canonicalUrl} />
        <meta
          key="og:description"
          property="og:description"
          content="募集を探す際はこちら。外資・日系トップ企業を目指す学生のための就職活動サイト「外資就活ドットコム」。トップ企業の新卒採用情報から、インターンシップ・先輩のエントリーシート・ES・体験記・就活コラム・TOEIC対策まで多様なコンテンツを掲載。"
        />
        <meta
          key="description"
          name="description"
          content="募集を探す際はこちら。外資・日系トップ企業を目指す学生のための就職活動サイト「外資就活ドットコム」。トップ企業の新卒採用情報から、インターンシップ・先輩のエントリーシート・ES・体験記・就活コラム・TOEIC対策まで多様なコンテンツを掲載。"
        />
        <meta
          key="keywords"
          name="keywords"
          content="外資・日系トップ企業,企業一覧,外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,ES・体験記,注目企業"
        />
        <link rel="canonical" href={canonicalUrl} key="canonical" />
      </Head>
      <Box className="flex flex-col relative w-full">
        <HeaderForSp
          industryCategories={industryCategories}
          filterCategories={filterCategories}
          filterGraduationYear={filterGraduationYear}
          filterRecruitmentTags={filterRecruitmentTags}
          filterRecruitmentTypes={filterRecruitmentTypes}
          recruitmentTags={recruitmentTags}
          recruitmentTypes={recruitmentTypes}
          recruitmentOrder={recruitmentOrder}
          graduationYearsHots={graduationYearsHots}
          onFilterCategoryChange={(id) => {
            handleFilterChange(id, setFilterCategories);
          }}
          onFilterRecruitmentTagChange={(id) => {
            handleFilterChange(id, setFilterRecruitmentTags);
          }}
          onFilterRecruitmentTypeChange={(id) => {
            handleFilterChange(id, setFilterRecruitmentTypes);
          }}
          onGraduationYearChange={setFilterGraduationYear}
          onOrderChange={setRecruitmentOrder}
          onFilterApply={() => {
            setIsDrawerOpen(false);
            setIsOrderDrawerOpen(false);
          }}
          isDrawerOpen={isDrawerOpen}
          onDrawerOpenChange={setIsDrawerOpen}
          isOrderDrawerOpen={isOrderDrawerOpen}
          onOrderDrawerOpenChange={setIsOrderDrawerOpen}
        />
        <Box className="bg-background-1 grid">
          {!initialized
            ? defaultRecruitments?.map((recruitment, recruitmentIndex) => (
                <React.Fragment key={recruitmentIndex}>
                  {recruitment && (
                    <CardForSp
                      key={recruitment?.id}
                      recruitment={recruitment}
                      className="border-t-2"
                    />
                  )}
                </React.Fragment>
              ))
            : null}
          {isLoading ? (
            <Box className="w-full h-[50px] bg-black-5">
              {Array.from({length: limit}).map((_, index) => (
                <CardSkeltonSP key={index} />
              ))}
            </Box>
          ) : data?.recruitments ? (
            data.recruitments.length > 0 ? (
              data.recruitments.map((recruitment, recruitmentIndex) => (
                <React.Fragment key={recruitmentIndex}>
                  {recruitment && (
                    <CardForSp
                      key={recruitment?.id}
                      recruitment={recruitment}
                      className="border-t-2 border-line"
                    />
                  )}
                  {currentPage === 1 &&
                    isValid &&
                    getBannerPositionsInRecruitInfoForSp(recruitmentIndex) && (
                      <div className="bg-white w-full p-20 flex justify-center border-t-2 border-line">
                        <SpTopBottomBanner
                          type={getBannerPositionsInRecruitInfoForSp(
                            recruitmentIndex,
                          )}
                        />
                      </div>
                    )}
                </React.Fragment>
              ))
            ) : (
              <Box className="p-20 text-base text-black-2">
                一致する募集情報は見つかりませんでした。
              </Box>
            )
          ) : null}
        </Box>
        <Pagination
          className="mx-auto mt-8"
          value={currentPage}
          total={lastPage}
          onChange={(value: number) => {
            handlePageChange(value);
          }}
        />
      </Box>
    </>
  );
};
